import { Injectable } from '@angular/core';
import { SurveyModel, QuestionSelectBase } from 'survey-angular';
import { AccountAssessment } from '../models/AssessmentModel';

@Injectable()
export class AssessmentService {

	constructor() { }

  static nistFunctionNames = {
    ID: 'IDENTIFY',
    PR: 'PROTECT',
    DE: 'DETECT',
    RS: 'RESPOND',
    RC: 'RECOVER'
  }
  static nistCategoriesNames = {
    'ID.AM': 'Asset Management',
    'ID.BE': 'Business Environment',
    'ID.GV': 'Governance',
    'ID.RA': 'Risk Assessment',
    'ID.RM': 'Risk Management Stategy',
    'ID.SC': 'Supply Chain Risk Management',
    'PR.AC': 'Identify Management and Access Control',
    'PR.AT': 'Awareness and Training',
    'PR.DS': 'Data Security',
    'PR.IP': 'Information Protection Processes & Procedures',
    'PR.MA': 'Maintenance',
    'PR.PT': 'Protective Technology',
    'DE.AE': 'Anomalies and Events',
    'DE.CM': 'Security Continuous Monitoring',
    'DE.DP': 'Detection Processes',
    'RS.RP': 'Response Planning',
    'RS.CO': 'Communications',
    'RS.AN': 'Analysis',
    'RS.MI': 'Mitigation',
    'RS.IM': 'Improvements',
    'RC.RP': 'Recovery Planning',
    'RC.IM': 'Improvements',
    'RC.CO': 'Communications'
  };

	getNistFunctionColor(nistFunction) {
		const color = {
			contrast: '#fff',
			rgb: '221, 221, 221'
		};

    switch (nistFunction.toLowerCase()) {
      case 'id':
      case 'identify':
				color.contrast = '#fff';
				color.rgb = '102, 153, 255';
				break;
      case 'pr':
      case 'protect':
				color.contrast = '#fff';
				color.rgb = '153, 102, 255';
				break;
      case 'de':
      case 'detect':
				color.contrast = '#000';
				color.rgb = '255, 204, 102';
        break;
      case 'rs':
			case 'respond':
				color.contrast = '#000';
				color.rgb = '255, 102, 102';
        break;
      case 'rc':
			case 'recover':
				color.contrast = '#000';
				color.rgb = '153, 255, 153';
				break;
			default:
				color.contrast = '#fff';
				color.rgb = '221, 221, 221'
		}

		return color;
	}

  getNistFunctions(accountAssessment: AccountAssessment): string[] {
		return Object.keys(accountAssessment.scoredResultsJson.nistFunctions);
	}

	getNistCategoryData(accountAssessment: AccountAssessment, riskSection?: string): any[] {
		let newNistCategoryData = [];

		Object.keys(accountAssessment.scoredResultsJson.nistFunctions).forEach(nistFunctionKey => {
      const nistFunction = accountAssessment.scoredResultsJson.nistFunctions[nistFunctionKey];
      if (riskSection == undefined || riskSection == nistFunctionKey) {
        Object.keys(nistFunction.nistCategories).forEach(nistCategoryKey => {
          const nistCategory = nistFunction.nistCategories[nistCategoryKey];
          const obj = {
            id: nistCategoryKey,
            label: nistCategory.name,
            scoreData: nistCategory
					}
					newNistCategoryData.push(obj);
				});
			}
		})
		newNistCategoryData = newNistCategoryData.sort((a, b): number => {
			return a.scoreData.impact - b.scoreData.impact;
		}).reverse();

		return newNistCategoryData;
  }

  getNistSubCategoryData(accountAssessment: AccountAssessment, nistFunctionId?: string): any[] {
    let newNistSubCategoryData = [];

    Object.keys(accountAssessment.scoredResultsJson.nistFunctions).forEach(nistFunctionKey => {
      const nistFunction = accountAssessment.scoredResultsJson.nistFunctions[nistFunctionKey];
      if (nistFunctionId == undefined || nistFunctionId == nistFunctionKey) {
        Object.keys(nistFunction.nistCategories).forEach(nistCategoryKey => {
          const nistCategory = nistFunction.nistCategories[nistCategoryKey];
          Object.keys(nistCategory.nistSubCategories).forEach(nistSubCategoryKey => {
            const nistSubCategory = nistCategory.nistSubCategories[nistSubCategoryKey];

            const obj = {
              id: nistSubCategoryKey,
              text: nistSubCategory.text,
              label: nistSubCategoryKey,
              scoreData: nistSubCategory
            }
            newNistSubCategoryData.push(obj);
          });
        });
      }
    })
    newNistSubCategoryData = newNistSubCategoryData.sort((a, b): number => {
      return a.scoreData.impact - b.scoreData.impact;
    }).reverse();

    return newNistSubCategoryData;
  }

  getNistCategoryQuestions(accountAssessment: AccountAssessment, nistCatgoryKey: string): any[] {
    const nistCategory = accountAssessment.scoredResultsJson.nistFunctions[nistCatgoryKey.substr(0, 2)].nistCategories[nistCatgoryKey];
    const arrInitialValue: any[] = [];
    return Object.values(nistCategory.nistSubCategories).map(x => (x as any).questions).reduce<any[]>((sumVal, val) => sumVal.concat(val), arrInitialValue);
  }

  getRiskLabel(nistSubCategoryScore) {
    const rank = nistSubCategoryScore.score
    let risk;
    if (rank >= 70) {
        risk = 'Low';
    } else if (rank >= 50) {
        risk = 'Medium';
    } else if (rank >= 30) {
        risk = 'High';
    } else {
        risk = 'Critical';
    }
    return risk;
  }

  getRiskColor(nistSubCategoryScore) {
    const risk = this.getRiskLabel(nistSubCategoryScore);
    return this.getRiskColorByLabel(risk);
  }

  getRiskColorByLabel(risk) {
    let color;

    switch (risk) {
      case 'Low':
        color = 'rgb(91, 186, 71)';
        break;
      case 'Medium':
        color = 'rgb(155, 178, 0)';
        break;
      case 'High':
        color = 'rgb(216, 164, 10)';
        break;
      case 'Critical':
        color = 'rgb(229, 58, 53)';
        break;
      default:
        color = 'rgb(196,196,196)';
        break;
    }
    return color;
  }
}

export class NistID {
  // "RC.CO-3"
  get nistFunction(): string {
    return this.id.substr(0, 2);
  }
  get nistCategory(): string {
    return this.id.substr(0, 5);
  }
  constructor(
    public id: string) {
  }
}

export class NistAssetValues {
  people: number;
  equipment: number;
  environment: number;
  software: number;
  data: number;
  informationSystems: number;
  organization: number;
  thirdParties: number;
  all: number;

  constructor(mult) {
    this.people = 250000 * mult,
    this.equipment = 70000 * mult,
    this.environment = 100000 * mult,
    this.software = 40000 * mult,
    this.data = 500000 * mult,
    this.informationSystems = 75000 * mult,
    this.organization = 250000 * mult,
    this.thirdParties = 250000 * mult,
    this.all = 1000000 * mult
  }
}
